import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { EMPTY, Subscription, of, switchMap, take, takeUntil } from 'rxjs';
import { FileObject, mapMediaToFileObject } from 'src/app/core/interfaces';
import { editedValues, restoreEdits } from 'src/app/core/utils/form-edits';
import { withUnsavedMedia } from '../patient-note-detail/note-media';
import { BaseComponent } from '../../../../core/components/base/base.component';
import { patientGroupId } from '../../../../core/utils/patient-group';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { GroupService } from '../../../../core/services/group.service';
import { PatientNoteService } from '../../../../core/services/patient-note.service';
import { PatientService } from '../../../../core/services/patient.service';
import { DeceasedAppointmentCheckService } from '../../../../core/services/deceased-appointment-check.service';
import * as _ from 'lodash';
import { MORBIDITY_MORTALITY_REASONS } from 'src/app/core/data/app.data';

@Component({
  selector: 'app-patient-morbidity-mortality-detail',
  templateUrl: './patient-morbidity-mortality-detail.component.html',
  styleUrls: ['./patient-morbidity-mortality-detail.component.scss']
})
export class PatientMorbidityMortalityDetailComponent extends BaseComponent implements OnInit, OnDestroy {
  todayDate: moment.Moment = moment();

  form!: FormGroup;
  editingNote: any;
  patient: any = {};
  groupId!: string;
  noteMediaObjects: FileObject[] = [];
  loadedNoteMediaObjects: FileObject[] = [];
  /** The note whose media is on the page, so a patch of another note does not inherit this one's unsaved files. */
  private mediaPatchedFor: string | null = null;

  mmReasons: string[] = MORBIDITY_MORTALITY_REASONS;
  private hasShownAppointmentDialog: boolean = false;

  private routeParamsSub!: Subscription;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private groupService: GroupService,
    private patientService: PatientService,
    private noteService: PatientNoteService,
    private deceasedAppointmentCheckService: DeceasedAppointmentCheckService,
    private cd: ChangeDetectorRef,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    this.initForm();

    this.routeParamsSub = this.route.paramMap
      .pipe(
        takeUntil(this.destroy$),
        switchMap(params => {
          const noteId = params.get('id');
          if (noteId) {
            // Use case 2: Edit an existing note
            return this.noteService.getNoteById(noteId)
          } else {
            // Use case 1 or 3: New note
            return of(null);
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(note => {

        if (note && note.data) {
          // Use case 2: Patch the form with the retrieved note data
          this.editingNote = note.data;
          this.patchForm({ keepEdits: true });
        } else {

          // Use case 3: Check for patientID
          this.route.queryParams
            .pipe(
              takeUntil(this.destroy$),
              take(1)
            ).subscribe(params => {
              const patientId = params['patientId'];
              if (patientId) {
                // Use case 3: Patch the form with patientId
                setTimeout(() => {
                  this.form.patchValue({ patientId });
                })
              }
            });
        }
      });

    this.groupService.selectedGroup$
      .pipe(
        switchMap(selectedGroup => {
          if (selectedGroup) {
            this.groupId = selectedGroup.id;
            this.form.patchValue({ groupId: patientGroupId(this.patient, this.groupId) });
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();


    this.patientService.currentPatient$
      .pipe(
        switchMap(selectedPatient => {
          if (selectedPatient) {
            this.patient = selectedPatient;
            this.form.patchValue({ patientId: this.patient.id, groupId: patientGroupId(this.patient, this.groupId) });
          } /* else {
            this.returnToParent();
          } */
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();

    this.noteService.editingNote$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(note => {
        if (note) {
          this.editingNote = note;
          this.patchForm({ keepEdits: true });
        } else {
          this.resetForm()
        }

      });

  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.hasShownAppointmentDialog = false; // Reset the flag when component is destroyed
    if (this.routeParamsSub) {
      this.routeParamsSub.unsubscribe();
    }
  }

  initForm() {
    this.form = this.fb.group({
      date: [moment(), Validators.required],
      groupId: [patientGroupId(this.patient, this.groupId), Validators.required],
      patientId: [this.patient.id, Validators.required],
      noteType: [''],
      codingBundle: [''],
      noteTypeHeadings: [''],
      title: ['', Validators.required],
      notes: [''],
      isProcedure: [false],
      procedureCodes: [[]],
      isMorbidity: [true],
      isMortality: [false],
    });
  }

  /**
   * Fills the form from `editingNote`. The note is filled from the timeline's copy and again when its fetch lands;
   * with `keepEdits`, fields typed in between stay as typed (not across notes).
   */
  patchForm({ keepEdits = false }: { keepEdits?: boolean } = {}) {
    if (!this.editingNote) {
      return; // Guard clause in case this.editingNote is not yet set
    }
    const otherNote = !!this.mediaPatchedFor && this.mediaPatchedFor !== this.editingNote.id;
    const edits = keepEdits && !otherNote ? editedValues(this.form) : {};
    if (otherNote) this.form.markAsPristine();
    // Basic patching for directly matched fields
    this.form.patchValue(this.editingNote);

    // For any other special handling or transformations, continue below
    if (this.editingNote.date) {
      this.form.patchValue({ date: moment(this.editingNote.date) });
    }


    // The note's media, plus anything pasted or picked before this (re)patch: the note is patched once from the
    // timeline and again when its fetch lands, and that second patch used to drop a picture pasted in between.
    // Not across notes: opening another one starts from its own media.
    const unsaved = this.mediaPatchedFor && this.mediaPatchedFor !== this.editingNote.id ? [] : this.noteMediaObjects;
    this.mediaPatchedFor = this.editingNote.id;
    this.noteMediaObjects = withUnsavedMedia(this.editingNote.media ? mapMediaToFileObject(this.editingNote.media) : [], unsaved);
    this.loadedNoteMediaObjects = [...this.noteMediaObjects];
    restoreEdits(this.form, edits);

  }

  toggleMorbidity(event: MatSlideToggleChange) {
    this.form.patchValue({ isMorbidity: event.checked });
    this.form.patchValue({ isMortality: !event.checked });
  }

  toggleMortality(event: MatSlideToggleChange) {
    this.form.patchValue({ isMortality: event.checked });
    this.form.patchValue({ isMorbidity: !event.checked });
  }

  resetForm() {
    setTimeout(() => {
      this.editingNote = null;
      this.noteMediaObjects = [];
      this.loadedNoteMediaObjects = [];
      this.mediaPatchedFor = null;
      this.hasShownAppointmentDialog = false; // Reset the flag for new notes
      this.form.reset();
      this.initForm();
      this.cd.detectChanges();
    });
  }

  onNoteMediaUpload(files: FileObject[]) {
    const newMediaObjects = files.map(file => ({
      ...file,
      //url: URL.createObjectURL(file),
      type: file.type,
    }));

    // Check for duplicates and avoid appending unnecessarily
    const uniqueMediaObjects = newMediaObjects.filter(newObj =>
      !this.noteMediaObjects.some(existingObj => existingObj.url === newObj.url)
    );

    this.noteMediaObjects = [...this.noteMediaObjects, ...uniqueMediaObjects];
  }

  onFilesEdited(files: any) {
    this.noteMediaObjects = files as FileObject[];
  }

  onNoteMediaDelete(fileObject: FileObject) {
    const index = this.noteMediaObjects.findIndex(obj => obj.url === fileObject.url);
    if (index > -1) {
      this.noteMediaObjects.splice(index, 1);
      if (fileObject.id) {
        this.noteService.deleteNoteMedia(fileObject.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.feedbackService.showToast(`Note media deleted successfully.`);
            },
            error: (error) => {
              this.feedbackService.showToast(`There was an error deleting the note media. Please try again.`);
            }
          });
      }
    }
  }

  saveNote() {
    const existingMediaFiles = this.editingNote?.media || [];
    const newMediaFiles = this.noteMediaObjects.filter(obj => !obj.id);

    const date: moment.Moment = this.form.value.date;

    const noteData = {
      ...this.form.value,
      existingMedia: existingMediaFiles,
      date: date.utc().startOf('day').toISOString()
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(noteData));


    const mediaAppendPromises = newMediaFiles.map((obj, index) => {
      return fetch(obj.url)
        .then(response => response.blob())
        .then(blob => {
          formData.append(`newMedia${index}`, blob, `${moment().unix()}.${obj.type.split('/')[1]}`);
        });
    });

    Promise.all(mediaAppendPromises)
      .then(() => {
        if (this.editingNote && this.editingNote.id) {
          this.noteService.updateNote(this.editingNote.id, formData)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (result) => {
                this.showToastMessage('Morbidity/Mortality note updated successfully');

                // Check for future appointments if this is a mortality note
                if (this.form.value.isMortality) {
                  this.checkForFutureAppointments();
                }

                this.returnToParent();

              },
              error: (error) => {
                console.error('Error updating Morbidity/Mortality note: ', error);
                this.showToastMessage('There was an error updating the patient note. Please try again.');
              }
            });
        } else {
          this.noteService.addNote(formData)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (result) => {
                this.showToastMessage('Morbidity/Mortality note saved successfully');

                // Check for future appointments if this is a mortality note
                if (this.form.value.isMortality) {
                  this.checkForFutureAppointments();
                }
                
                this.returnToParent();

              },
              error: (error) => {
                console.error('Error saving Morbidity/Mortality note: ', error);
                this.showToastMessage('There was an error saving the patient note. Please try again.');
              }
            });
        }
      });
  }


  async deleteNote() {
    const confirmed = await this.showConfirmationDialog();
    if (!confirmed) return;

    this.noteService.deleteNote(this.editingNote.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Morbidity/Mortality note deleted successfully');
          this.returnToParent()
        },
        error: (error) => {
          console.error('Error saving Morbidity/Mortality note: ', error);
          this.showToastMessage('There was an error deleting the patient note. Please try again.');
        }
      });
  }

  checkForFutureAppointments() {
    if (!this.patient?.id || this.hasShownAppointmentDialog) return;
    // Set on start rather than on prompt: the watch below lives until destroy$ and
    // prompts at most once, so this only stops a second, duplicate watch starting.
    this.hasShownAppointmentDialog = true;

    this.deceasedAppointmentCheckService
      .watchForFutureAppointments(this.patient.id, this.destroy$)
      .subscribe(outcome => {
        switch (outcome.action) {
          case 'review':
            // Review appointments - scroll to appointments section
            this.scrollToAppointments();
            break;
          case 'cancelled':
          case 'ignored':
          case 'dismissed':
            this.returnToParent();
            break;
          // 'kept' - user backed out of cancelling, stay put
        }
      });
  }

  scrollToAppointments() {
    // Navigate to patient file and scroll to appointments section
    this.deceasedAppointmentCheckService.scrollToPatientAppointments(this.patient.id);
  }

  returnToParent() {
    if (this.patient?.id) {
      this.router.navigate(['/patients', { outlets: { patient: ['file', `${this.patient.id}`] } }]);
    } else {
      let parentPath = 'list';
      const currentUrl = this.router.url;
      if (currentUrl.includes('schedule')) {
        parentPath = 'schedule';
      }
      if (currentUrl.includes('filters')) {
        parentPath = 'filters';
      }
      if (currentUrl.includes('coding')) {
        parentPath = 'coding';
      }
      this.router.navigate(['/patients', { outlets: { patient: null } }]).then(() => {
        this.router.navigate(['/patients', parentPath]);
      });
    }
  }




}
